/**
 * PR-H — OSNAP marker (14 modes).
 *
 * One world-space `THREE.Group` mounted in the scene; each frame the
 * caller updates its (mode, world target) and the group swaps in the
 * pre-computed geometry for the active mode. World scale tracks
 * camera zoom so the glyph stays a constant `pickbox_px / cameraZoom`
 * in screen pixels.
 *
 * # Modes (14, from `objectSnapModeDefinitions` + `none`)
 *
 *   1.  endpoint              — yellow square
 *   2.  midpoint              — yellow triangle (point up)
 *   3.  intersection          — yellow X
 *   4.  apparent-intersection — yellow X with offset notch
 *   5.  center                — yellow circle
 *   6.  geometric-center      — yellow circle (same glyph; differs by note)
 *   7.  quadrant              — yellow diamond
 *   8.  tangent               — yellow circle with tangent line
 *   9.  perpendicular         — yellow square with right-angle mark
 *  10.  node                  — yellow circle-with-X
 *  11.  insertion             — yellow square-with-arrow
 *  12.  nearest               — yellow hourglass
 *  13.  parallel              — yellow double-line
 *  14.  extension             — yellow plus on dashed extension line
 *  15.  none                  — no marker (off mode)
 *
 * Plan §PR-H lists 14 distinct shapes; we keep `geometric-center`
 * sharing the `center` circle to match AutoCAD parity (both render the
 * same glyph; only the prompt label differs). That keeps our marker
 * dictionary at 14 distinct geometries (one per `SnapMarkerMode`).
 *
 * # Construction
 *
 * Geometries are pre-built once at module init time via
 * `buildMarkerGeometries()` and stored in a frozen registry. Each
 * geometry uses unit world-space (1×1 box centred at origin); the
 * `THREE.Group` that owns the active marker scales the glyph via
 * `group.scale.setScalar(markerWorldSize)` to size it on screen.
 *
 * # Memory
 *
 * 14 geometries × ~6-12 vertices each = trivial. The registry is held
 * for the lifetime of the renderer; `disposeMarkerGeometries()` is
 * exposed for hot-reload but not called in steady-state.
 */

import * as THREE from "three";

/**
 * Identifier of one OSNAP marker glyph. Mirrors
 * `apps/web/src/app/scene/objectSnaps.ts:ObjectSnapMode` plus a
 * `none` sentinel for the off state.
 */
export type SnapMarkerMode =
  | "endpoint"
  | "midpoint"
  | "intersection"
  | "apparent-intersection"
  | "center"
  | "geometric-center"
  | "quadrant"
  | "tangent"
  | "perpendicular"
  | "node"
  | "insertion"
  | "nearest"
  | "parallel"
  | "extension"
  | "none";

/**
 * Pickbox CSS-pixel size (AutoCAD `PICKBOX` sysvar default = 8 px).
 * The marker world-size = `PICKBOX / cameraZoom`, scaled up by 2 so
 * the glyph reads at PICKBOX × 2 pixels (16 px) — matches the SVG
 * stage's `9 / cameraZoom` snap circle.
 */
export const SNAP_MARKER_PICKBOX_PX = 8;
export const SNAP_MARKER_GLYPH_PX = SNAP_MARKER_PICKBOX_PX * 2;

/** Yellow snap-marker colour, matches AutoCAD's default OSNAP indicator. */
export const SNAP_MARKER_COLOR = "#f1c40f";

/**
 * One marker geometry. We use `LineSegments` (not `Mesh`) for every
 * glyph because each shape is line-art; the glyph is therefore drawn
 * as outlines on transparent background (matches AutoCAD parity).
 *
 * Some glyphs (`extension`) include a separate dashed line — these
 * carry an extra `dashedSegments` field that lives on the same
 * geometry but is rendered with a dashed material.
 */
export interface MarkerGeometryEntry {
  /** Solid line segments (one entry per pair of endpoints). */
  segments: Float32Array;
  /** Dashed line segments (extension only). Empty for other modes. */
  dashedSegments: Float32Array;
}

/**
 * Build a square (endpoint) — 4 line segments forming the outline of
 * a 1×1 box centred at origin.
 */
function buildSquare(): Float32Array {
  const h = 0.5;
  return new Float32Array([
    -h, -h, 0,  h, -h, 0,
     h, -h, 0,  h,  h, 0,
     h,  h, 0, -h,  h, 0,
    -h,  h, 0, -h, -h, 0,
  ]);
}

/**
 * Build a triangle (midpoint), point up. 3 segments forming an
 * isoceles triangle bounded by the unit box.
 */
function buildTriangle(): Float32Array {
  const h = 0.5;
  return new Float32Array([
    -h, -h, 0,  h, -h, 0, // bottom
     h, -h, 0,  0,  h, 0, // right side
     0,  h, 0, -h, -h, 0, // left side
  ]);
}

/**
 * Build an X (intersection). 2 segments (corner-to-corner cross).
 */
function buildCross(): Float32Array {
  const h = 0.5;
  return new Float32Array([
    -h, -h, 0,  h,  h, 0,
    -h,  h, 0,  h, -h, 0,
  ]);
}

/**
 * Build an X with an offset notch (apparent-intersection). The two
 * crossing segments are slightly displaced from each other to convey
 * the "apparent (projected)" semantic — they no longer share a single
 * crossing point.
 */
function buildCrossWithNotch(): Float32Array {
  const h = 0.5;
  const o = 0.08; // notch offset
  return new Float32Array([
    -h,     -h + o, 0,  h - o,  h,     0, // upper diagonal
    -h + o, -h,     0,  h,      h - o, 0, // notch'd diagonal
    -h,      h,     0,  h,     -h,     0, // counter-diagonal
  ]);
}

/**
 * Build a circle (center) — N-segment polyline approximating a
 * circumscribed unit circle. 24 segments is enough for a smooth
 * appearance at PICKBOX × 2 pixels; cheap to render.
 */
function buildCircle(segments = 24): Float32Array {
  const r = 0.5;
  const out = new Float32Array(segments * 6);
  for (let i = 0; i < segments; i++) {
    const a0 = (i / segments) * Math.PI * 2;
    const a1 = ((i + 1) / segments) * Math.PI * 2;
    out[i * 6] = Math.cos(a0) * r;
    out[i * 6 + 1] = Math.sin(a0) * r;
    out[i * 6 + 2] = 0;
    out[i * 6 + 3] = Math.cos(a1) * r;
    out[i * 6 + 4] = Math.sin(a1) * r;
    out[i * 6 + 5] = 0;
  }
  return out;
}

/**
 * Build a diamond (quadrant). 4 segments; rotated square.
 */
function buildDiamond(): Float32Array {
  const h = 0.5;
  return new Float32Array([
     0,  h, 0,  h,  0, 0,
     h,  0, 0,  0, -h, 0,
     0, -h, 0, -h,  0, 0,
    -h,  0, 0,  0,  h, 0,
  ]);
}

/**
 * Build a circle with a horizontal tangent line at the top — the
 * "tangent" snap glyph. Tangent line spans the unit width.
 */
function buildCircleWithTangent(): Float32Array {
  const circle = buildCircle(24);
  const h = 0.5;
  const tangent = new Float32Array([
    -h, h, 0,  h, h, 0,
  ]);
  const out = new Float32Array(circle.length + tangent.length);
  out.set(circle, 0);
  out.set(tangent, circle.length);
  return out;
}

/**
 * Build a square with a small right-angle mark inside (perpendicular).
 * The right-angle is a small "L" in the lower-left quadrant of the
 * square — matches AutoCAD's perpendicular glyph.
 */
function buildSquareWithRightAngle(): Float32Array {
  const square = buildSquare();
  const angle = new Float32Array([
    -0.25, -0.5, 0,  -0.25, -0.05, 0, // vertical
    -0.25, -0.05, 0,   0.25, -0.05, 0, // horizontal
  ]);
  const out = new Float32Array(square.length + angle.length);
  out.set(square, 0);
  out.set(angle, square.length);
  return out;
}

/**
 * Build a circle-with-X (node). Circle + cross overlay.
 */
function buildCircleWithCross(): Float32Array {
  const circle = buildCircle(24);
  const cross = buildCross();
  const out = new Float32Array(circle.length + cross.length);
  out.set(circle, 0);
  out.set(cross, circle.length);
  return out;
}

/**
 * Build a square with an arrow (insertion). Square outline + small
 * arrow pointing diagonally into the upper-right corner.
 */
function buildSquareWithArrow(): Float32Array {
  const square = buildSquare();
  const arrow = new Float32Array([
     0.0,  0.0, 0,  0.5,  0.5, 0,  // shaft
     0.5,  0.5, 0,  0.5,  0.2, 0,  // arrowhead leg 1
     0.5,  0.5, 0,  0.2,  0.5, 0,  // arrowhead leg 2
  ]);
  const out = new Float32Array(square.length + arrow.length);
  out.set(square, 0);
  out.set(arrow, square.length);
  return out;
}

/**
 * Build an hourglass (nearest). Two triangles meeting at the centre,
 * top and bottom. Matches AutoCAD's nearest-snap glyph.
 */
function buildHourglass(): Float32Array {
  const h = 0.5;
  return new Float32Array([
    -h, -h, 0,  h, -h, 0,  // bottom edge
     h, -h, 0, -h,  h, 0,  // diagonal 1
    -h,  h, 0,  h,  h, 0,  // top edge
     h,  h, 0, -h, -h, 0,  // diagonal 2
  ]);
}

/**
 * Build a double-line (parallel). Two parallel horizontal segments.
 */
function buildDoubleLine(): Float32Array {
  const h = 0.5;
  return new Float32Array([
    -h, -0.18, 0,  h, -0.18, 0,
    -h,  0.18, 0,  h,  0.18, 0,
  ]);
}

/**
 * Build a plus sign (extension marker). Solid plus + dashed line is
 * supplied separately by `dashedSegments`.
 */
function buildPlusSegments(): Float32Array {
  return new Float32Array([
    -0.5,  0.0, 0,  0.5,  0.0, 0, // horizontal
     0.0, -0.5, 0,  0.0,  0.5, 0, // vertical
  ]);
}

/**
 * Build the dashed extension line for the extension marker — extends
 * 2 unit-lengths to the right of the plus glyph, drawn with the
 * dashed material instead of the solid one.
 */
function buildExtensionDashedLine(): Float32Array {
  return new Float32Array([
    0.5, 0, 0,  2.5, 0, 0,
  ]);
}

const EMPTY_DASHED = new Float32Array(0);

/**
 * Marker geometry registry. Built once at module init via
 * `buildMarkerGeometries()` and treated as immutable.
 *
 * Exposing the registry as a function (not a top-level constant) lets
 * unit tests inspect the contents without forcing the THREE/Float32
 * allocator to run at import time on the SSR / test path.
 */
export function buildMarkerGeometries(): Record<
  SnapMarkerMode,
  MarkerGeometryEntry
> {
  return {
    endpoint: { segments: buildSquare(), dashedSegments: EMPTY_DASHED },
    midpoint: { segments: buildTriangle(), dashedSegments: EMPTY_DASHED },
    intersection: { segments: buildCross(), dashedSegments: EMPTY_DASHED },
    "apparent-intersection": {
      segments: buildCrossWithNotch(),
      dashedSegments: EMPTY_DASHED,
    },
    center: { segments: buildCircle(24), dashedSegments: EMPTY_DASHED },
    "geometric-center": {
      segments: buildCircle(24),
      dashedSegments: EMPTY_DASHED,
    },
    quadrant: { segments: buildDiamond(), dashedSegments: EMPTY_DASHED },
    tangent: {
      segments: buildCircleWithTangent(),
      dashedSegments: EMPTY_DASHED,
    },
    perpendicular: {
      segments: buildSquareWithRightAngle(),
      dashedSegments: EMPTY_DASHED,
    },
    node: { segments: buildCircleWithCross(), dashedSegments: EMPTY_DASHED },
    insertion: {
      segments: buildSquareWithArrow(),
      dashedSegments: EMPTY_DASHED,
    },
    nearest: { segments: buildHourglass(), dashedSegments: EMPTY_DASHED },
    parallel: { segments: buildDoubleLine(), dashedSegments: EMPTY_DASHED },
    extension: {
      segments: buildPlusSegments(),
      dashedSegments: buildExtensionDashedLine(),
    },
    none: { segments: EMPTY_DASHED, dashedSegments: EMPTY_DASHED },
  };
}

/**
 * Snap marker handle returned by `createSnapMarker`. The caller
 * mounts `group` into the scene, then calls `setActive(mode, world,
 * cameraZoom)` each time the snap target moves; `setHidden()` clears
 * the marker without removing the group.
 *
 * The group's children are pre-built `LineSegments` per mode; only the
 * active mode's child is set `visible = true` per frame.
 */
export interface SnapMarkerHandle {
  /** Top-level group to add to the scene. */
  group: THREE.Group;
  /** Show the marker for the given mode at the given world point. */
  setActive(
    mode: SnapMarkerMode,
    world: { x: number; y: number },
    cameraZoom: number,
  ): void;
  /** Hide the marker (e.g., when no snap target is active). */
  setHidden(): void;
  /** Dispose all owned geometries / materials. */
  dispose(): void;
}

/**
 * Create the OSNAP marker group + per-mode child meshes. One mesh per
 * mode is added eagerly; the group toggles visibility instead of
 * remounting children, so per-frame cost is O(1).
 *
 * The solid material is shared across modes; the dashed material (used
 * by the `extension` mode only) is also shared.
 */
export function createSnapMarker(): SnapMarkerHandle {
  const group = new THREE.Group();
  group.userData = { kind: "snap-marker" };
  group.visible = false;
  // Markers paint over scene contents — high render order, no depth
  // testing so they never clip behind hatches / wipeouts.
  group.renderOrder = 100;

  const solidMaterial = new THREE.LineBasicMaterial({
    color: new THREE.Color(SNAP_MARKER_COLOR),
    transparent: false,
    depthTest: false,
    depthWrite: false,
  });
  // Dashed: needs gapSize/dashSize. Distance arr is precomputed via
  // `computeLineDistances`; dash + gap are world-units (unit = 1 in
  // marker-local space, scaled by `group.scale` to camera zoom).
  const dashedMaterial = new THREE.LineDashedMaterial({
    color: new THREE.Color(SNAP_MARKER_COLOR),
    dashSize: 0.2,
    gapSize: 0.1,
    transparent: false,
    depthTest: false,
    depthWrite: false,
  });

  const registry = buildMarkerGeometries();
  const childByMode = new Map<SnapMarkerMode, THREE.Group>();

  for (const mode of Object.keys(registry) as SnapMarkerMode[]) {
    const entry = registry[mode];
    const child = new THREE.Group();
    child.visible = false;
    child.renderOrder = 100;
    if (entry.segments.length > 0) {
      const geom = new THREE.BufferGeometry();
      geom.setAttribute(
        "position",
        new THREE.BufferAttribute(entry.segments, 3),
      );
      const mesh = new THREE.LineSegments(geom, solidMaterial);
      mesh.frustumCulled = false;
      child.add(mesh);
    }
    if (entry.dashedSegments.length > 0) {
      const geom = new THREE.BufferGeometry();
      geom.setAttribute(
        "position",
        new THREE.BufferAttribute(entry.dashedSegments, 3),
      );
      const mesh = new THREE.LineSegments(geom, dashedMaterial);
      mesh.computeLineDistances();
      mesh.frustumCulled = false;
      child.add(mesh);
    }
    group.add(child);
    childByMode.set(mode, child);
  }

  let activeMode: SnapMarkerMode = "none";

  return {
    group,
    setActive(mode, world, cameraZoom) {
      // Mode-swap: hide previous, show new.
      if (mode !== activeMode) {
        const prev = childByMode.get(activeMode);
        if (prev) prev.visible = false;
        activeMode = mode;
      }
      const next = childByMode.get(mode);
      if (!next || mode === "none") {
        group.visible = false;
        return;
      }
      next.visible = true;
      group.visible = true;
      group.position.set(world.x, world.y, 0);
      const safeZoom = Math.max(cameraZoom, 1e-6);
      // PICKBOX × 2 px → world units; group.scale uniform.
      const worldSize = SNAP_MARKER_GLYPH_PX / safeZoom;
      group.scale.setScalar(worldSize);
      group.updateMatrixWorld(true);
    },
    setHidden() {
      group.visible = false;
      const child = childByMode.get(activeMode);
      if (child) child.visible = false;
      activeMode = "none";
    },
    dispose() {
      group.traverse((obj) => {
        const mesh = obj as THREE.LineSegments;
        if (mesh.geometry) mesh.geometry.dispose();
      });
      solidMaterial.dispose();
      dashedMaterial.dispose();
      childByMode.clear();
    },
  };
}
